'use client'

import Link from 'next/link'
import { useState } from 'react'

export default function ContactPage() {
  const year = new Date().getFullYear()
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState('')
  // honeypot
  const [website, setWebsite] = useState('')

  async function onSubmit(e) {
    e.preventDefault()
    setBusy(true); setNotice('')
    try {
      const r = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, subject, message, website })
      })
      const data = await r.json().catch(()=> ({}))
      if (!r.ok) throw new Error(data?.error || `HTTP ${r.status}`)
      setNotice('Thanks! Your message has been sent.')
      setName(''); setEmail(''); setSubject(''); setMessage(''); setWebsite('')
    } catch (err) {
      setNotice(err.message || 'Failed to send message.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Top Bar - matching home page style */}
      <header className="w-full py-4 px-6 flex items-center justify-between border-b border-gray-900/60">
        <Link href="/" className="flex items-center gap-3">
          <img
            src="/LFT_Icon_64x64.png"
            alt="Lucius Fox Technologies Logo"
            className="h-10 w-10 object-contain rounded"
          />
          <span className="text-lg font-semibold tracking-wide">Lucius Fox Technologies, LLC</span>
        </Link>
        <nav className="flex gap-6 text-sm font-medium">
          <Link href="/#services" className="hover:text-blue-400 transition">Services</Link>
          <Link href="/#industries" className="hover:text-blue-400 transition">Industries</Link>
          <Link href="/#work" className="hover:text-blue-400 transition">Selected Work</Link>
          <Link href="/about" className="hover:text-blue-400 transition">About</Link>
          <Link href="/backtest" className="hover:text-blue-400 transition">Trading</Link>
          <Link href="/government" className="hover:text-blue-400 transition">Government</Link>
          <Link href="/contact" className="text-blue-400">Contact</Link>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="px-6 pt-10 pb-12">
        <div className="max-w-6xl mx-auto">
          <div className="inline-flex items-center gap-2 rounded-lg border border-gray-800/80 bg-[#0d0d0d] px-3 py-1 text-xs text-gray-300">
            <span className="inline-block h-1.5 w-1.5 rounded-lg bg-[#1e40af]" />
            Get in Touch
          </div>
          <h1 className="text-3xl md:text-5xl font-bold leading-tight mt-3">
            Let's Build <span className="text-blue-400">Something Great</span>
          </h1>
          <p className="text-gray-300 mt-4 text-lg max-w-3xl">
            Whether you need a full-stack web application, data analytics pipeline, embedded IoT system,
            or government solutions—we're here to help. Share your project details and we'll get back to you promptly.
          </p>
        </div>
      </section>

      {/* Main Content - Form + Info */}
      <section className="px-6 py-12 border-t border-gray-900/60">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-8">

          {/* Contact Form */}
          <div className="rounded-2xl bg-[#0d0d0d] border border-gray-800/80 p-6">
            <h2 className="text-2xl font-semibold mb-4">Send a Message</h2>
            <form className="space-y-4" onSubmit={onSubmit}>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Name</label>
                <input
                  type="text"
                  placeholder="Your Name"
                  value={name}
                  onChange={e=>setName(e.target.value)}
                  required
                  className="w-full px-4 py-3 rounded-lg bg-black border border-gray-800 text-white focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Email</label>
                <input
                  type="email"
                  placeholder="your.email@example.com"
                  value={email}
                  onChange={e=>setEmail(e.target.value)}
                  required
                  className="w-full px-4 py-3 rounded-lg bg-black border border-gray-800 text-white focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Subject</label>
                <input
                  type="text"
                  placeholder="What's this about?"
                  value={subject}
                  onChange={e=>setSubject(e.target.value)}
                  required
                  className="w-full px-4 py-3 rounded-lg bg-black border border-gray-800 text-white focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                />
              </div>
              {/* honeypot field (hidden) */}
              <input
                type="text"
                value={website}
                onChange={e=>setWebsite(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
                className="hidden"
              />
              <div>
                <label className="block text-sm text-gray-400 mb-1">Message</label>
                <textarea
                  placeholder="Tell us about your project, timeline, and any specific requirements..."
                  rows="6"
                  value={message}
                  onChange={e=>setMessage(e.target.value)}
                  required
                  className="w-full px-4 py-3 rounded-lg bg-black border border-gray-800 text-white focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent resize-none"
                />
              </div>
              <button
                type="submit"
                disabled={busy}
                className="w-full bg-[#1e40af] text-white px-6 py-3 rounded-lg font-medium hover:brightness-110 transition disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {busy ? 'Sending…' : 'Send Message'}
              </button>
            </form>

            {notice && (
              <div className={`mt-4 p-3 rounded-lg ${notice.includes('Thanks') ? 'bg-emerald-900/20 text-emerald-400 border border-emerald-800' : 'bg-red-900/20 text-red-400 border border-red-800'}`}>
                <p className="text-sm">{notice}</p>
              </div>
            )}
          </div>

          {/* Contact Info & Details */}
          <div className="space-y-6">
            <div className="rounded-2xl bg-[#0d0d0d] border border-gray-800/80 p-6">
              <h2 className="text-2xl font-semibold mb-4">Contact Information</h2>
              <div className="space-y-4 text-sm">
                <div>
                  <div className="text-gray-400 mb-1">Company</div>
                  <div className="text-white font-medium">Lucius Fox Technologies, LLC</div>
                </div>
                <div>
                  <div className="text-gray-400 mb-1">Location</div>
                  <div className="text-white">Southern California</div>
                </div>
                <div>
                  <div className="text-gray-400 mb-1">Business Type</div>
                  <div className="text-white">Small Business • Engineering Firm</div>
                </div>
                <div>
                  <div className="text-gray-400 mb-1">Response Time</div>
                  <div className="text-white">Typically within 24 hours</div>
                </div>
              </div>
            </div>

            <div className="rounded-2xl bg-[#0d0d0d] border border-gray-800/80 p-6">
              <h3 className="font-semibold text-lg mb-3">What to Include</h3>
              <ul className="text-sm text-gray-300 space-y-2">
                <li>• <span className="text-white">Project scope:</span> What do you need built?</li>
                <li>• <span className="text-white">Timeline:</span> When do you need it?</li>
                <li>• <span className="text-white">Budget range:</span> What's your investment level?</li>
                <li>• <span className="text-white">Tech stack:</span> Any specific requirements?</li>
                <li>• <span className="text-white">Team size:</span> Solo project or team effort?</li>
              </ul>
            </div>

            <div className="rounded-2xl bg-[#0d0d0d] border border-gray-800/80 p-6">
              <h3 className="font-semibold text-lg mb-3">Quick Links</h3>
              <div className="space-y-2 text-sm">
                <Link href="/government" className="block text-blue-400 hover:underline">
                  → Government Solutions & Contracting
                </Link>
                <Link href="/about" className="block text-blue-400 hover:underline">
                  → About Us & Company Structure
                </Link>
                <Link href="/#work" className="block text-blue-400 hover:underline">
                  → View Our Work & Case Studies
                </Link>
                <Link href="/backtest" className="block text-blue-400 hover:underline">
                  → Trading Analytics Platform
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-6 py-10 border-t border-gray-900/60">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-gray-500">© {year} Lucius Fox Technologies, LLC. All rights reserved.</p>
          <div className="flex gap-4 text-sm">
            <Link href="/terms" className="text-gray-400 hover:text-gray-200">Terms</Link>
            <Link href="/privacy" className="text-gray-400 hover:text-gray-200">Privacy</Link>
            <Link href="/government" className="text-gray-400 hover:text-gray-200">Government</Link>
            <Link href="/contact" className="text-gray-400 hover:text-gray-200">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  )
}
